// Footer1.js - Contains functions for creating a Footer section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a complete Footer section with nested elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the footer is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The footer elements
 */
export const createFooterElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const footerSectionClass = 'footer1-section';
  const footerContainerClass = 'footer1-container';
  const footerTopClass = 'footer1-top';
  const footerColumnsClass = 'footer1-columns';
  const footerColumnClass = 'footer1-column';
  const footerLogoClass = 'footer1-logo';
  const footerDescriptionClass = 'footer1-description';
  const footerHeadingClass = 'footer1-heading';
  const footerLinksClass = 'footer1-links';
  const footerLinkClass = 'footer1-link';
  const footerSocialClass = 'footer1-social';
  const footerSocialLinkClass = 'footer1-social-link';
  const footerBottomClass = 'footer1-bottom';
  const footerCopyrightClass = 'footer1-copyright';

  // Desktop Styles (default)
  const footerSectionStyles = {
    position: 'relative',
    paddingTop: '60px',
    paddingBottom: '20px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#000000',
    color: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const footerContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    maxWidth: '1200px',
    width: '100%',
    margin: '0 auto',
    gap: '40px'
  };

  const footerTopStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    width: '100%'
  };

  const footerColumnsStyles = {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '30px'
  };

  const footerColumnStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '160px',
    flex: '1'
  };

  const footerLogoStyles = {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '16px',
    textDecoration: 'none'
  };

  const footerDescriptionStyles = {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#cccccc',
    maxWidth: '300px'
  };

  const footerHeadingStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: '16px'
  };

  const footerLinksStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  };

  const footerLinkStyles = {
    fontSize: '14px',
    color: '#cccccc',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    lineHeight: '1.4'
  };

  const footerSocialStyles = {
    display: 'flex',
    flexDirection: 'row',
    gap: '16px',
    marginTop: '8px'
  };

  const footerSocialLinkStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#333333',
    color: '#ffffff',
    transition: 'all 0.2s ease'
  };

  const footerBottomStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '24px',
    borderTop: '1px solid #333333',
    flexWrap: 'wrap',
    gap: '16px'
  };

  const footerCopyrightStyles = {
    fontSize: '14px',
    color: '#999999'
  };

  // Tablet Styles
  const tabletFooterColumnsStyles = {
    ...footerColumnsStyles,
    gap: '24px'
  };

  // Mobile Styles
  const mobileFooterColumnsStyles = {
    ...footerColumnsStyles,
    flexDirection: 'column',
    gap: '30px'
  };

  const mobileFooterColumnStyles = {
    ...footerColumnStyles,
    minWidth: '100%'
  };

  const mobileFooterBottomStyles = {
    ...footerBottomStyles,
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(footerSectionClass, footerSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerContainerClass, footerContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(footerTopClass, footerTopStyles);
    newRegistry[DEVICES.DESKTOP].set(footerColumnsClass, footerColumnsStyles);
    newRegistry[DEVICES.DESKTOP].set(footerColumnClass, footerColumnStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLogoClass, footerLogoStyles);
    newRegistry[DEVICES.DESKTOP].set(footerDescriptionClass, footerDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(footerHeadingClass, footerHeadingStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinksClass, footerLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(footerLinkClass, footerLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialClass, footerSocialStyles);
    newRegistry[DEVICES.DESKTOP].set(footerSocialLinkClass, footerSocialLinkStyles);
    newRegistry[DEVICES.DESKTOP].set(footerBottomClass, footerBottomStyles);
    newRegistry[DEVICES.DESKTOP].set(footerCopyrightClass, footerCopyrightStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(footerColumnsClass, tabletFooterColumnsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(footerColumnsClass, mobileFooterColumnsStyles);
    newRegistry[DEVICES.MOBILE].set(footerColumnClass, mobileFooterColumnStyles);
    newRegistry[DEVICES.MOBILE].set(footerBottomClass, mobileFooterBottomStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    footerSectionClass,
    footerContainerClass,
    footerTopClass,
    footerColumnsClass,
    footerColumnClass,
    footerLogoClass,
    footerDescriptionClass,
    footerHeadingClass,
    footerLinksClass,
    footerLinkClass,
    footerSocialClass,
    footerSocialLinkClass,
    footerBottomClass,
    footerCopyrightClass
  ]));

  // Generate IDs for all elements
  const footerSectionId = Date.now();
  const containerID = Date.now() + 1;
  const topID = Date.now() + 2;
  const columnsID = Date.now() + 3;
  
  // Column 1 - Logo and description
  const column1ID = Date.now() + 4;
  const logoID = Date.now() + 5;
  const descriptionID = Date.now() + 6;
  
  // Column 2 - Links
  const column2ID = Date.now() + 7;
  const heading1ID = Date.now() + 8;
  const links1ID = Date.now() + 9;
  const link1ID = Date.now() + 10;
  const link2ID = Date.now() + 11;
  const link3ID = Date.now() + 12;
  
  // Column 3 - Links
  const column3ID = Date.now() + 13;
  const heading2ID = Date.now() + 14;
  const links2ID = Date.now() + 15;
  const link4ID = Date.now() + 16;
  const link5ID = Date.now() + 17;
  const link6ID = Date.now() + 18;
  
  // Column 4 - Contact/Social
  const column4ID = Date.now() + 19;
  const heading3ID = Date.now() + 20;
  const socialLinksID = Date.now() + 21;
  const social1ID = Date.now() + 22;
  const social2ID = Date.now() + 23;
  const social3ID = Date.now() + 24;
  
  // Generate IDs for social media icons
  const social1IconId = Date.now() + 25;
  const social2IconId = Date.now() + 26;
  const social3IconId = Date.now() + 27;
  
  // Bottom section with copyright
  const bottomID = Date.now() + 28;
  const copyrightID = Date.now() + 29;

  const newElements = [
    // Main Footer Section Container
    {
      id: footerSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Footer Section',
      parentId: null,
      className: footerSectionClass,
      styles: {},
      children: [containerID],
      text: '',
      attributes: {
        'data-element': 'footer-section',
        'data-component': 'section',
        'data-section-type': 'footer',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Container',
      parentId: footerSectionId,
      hierarchyName: "Footer Container",
      className: footerContainerClass,
      styles: {},
      children: [topID, bottomID],
      text: '',
      attributes: {
        'data-element': 'footer-container'
      }
    },
    
    // Top Section
    {
      id: topID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Top',
      parentId: containerID,
      hierarchyName: "Footer Top",
      className: footerTopClass,
      styles: {},
      children: [columnsID],
      text: '',
      attributes: {
        'data-element': 'footer-top'
      }
    },
    
    // Columns Container
    {
      id: columnsID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Columns',
      parentId: topID,
      hierarchyName: "Footer Columns",
      className: footerColumnsClass,
      styles: {},
      children: [column1ID, column2ID, column3ID, column4ID],
      text: '',
      attributes: {
        'data-element': 'footer-columns'
      }
    },
    
    // Column 1 - Logo and Description
    {
      id: column1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 1',
      parentId: columnsID,
      hierarchyName: "Footer Column 1",
      className: footerColumnClass,
      styles: {},
      children: [logoID, descriptionID],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Logo
    {
      id: logoID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Logo',
      parentId: column1ID,
      hierarchyName: "Footer Logo",
      className: footerLogoClass,
      styles: {},
      children: [],
      text: 'YourBrand',
      attributes: {
        'data-element': 'footer-logo',
        'href': '#'
      },
      interactions: {
        events: [{
          id: Date.now(),
          type: 'animation',
          action: 'animate',
          trigger: 'onView',
          value: {
            animation: 'fade-in',
            duration: '1s',
            delay: '0.1s',
            iterations: '1'
          }
        }]
      }
    },
    
    // Description
    {
      id: descriptionID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Description',
      parentId: column1ID,
      hierarchyName: "Footer Description",
      className: footerDescriptionClass,
      styles: {},
      children: [],
      text: 'We create beautiful, fast and reliable digital products designed to help you grow your business and achieve your goals.',
      attributes: {
        'data-element': 'footer-description'
      }
    },
    
    // Column 2 - Links
    {
      id: column2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 2',
      parentId: columnsID,
      hierarchyName: "Footer Column 2",
      className: footerColumnClass,
      styles: {},
      children: [heading1ID, links1ID],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Heading 1
    {
      id: heading1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Footer Heading 1',
      parentId: column2ID,
      hierarchyName: "Footer Heading 1",
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Company',
      attributes: {
        'data-element': 'footer-heading'
      }
    },
    
    // Links 1
    {
      id: links1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Links 1',
      parentId: column2ID,
      hierarchyName: "Footer Links 1",
      className: footerLinksClass,
      styles: {},
      children: [link1ID, link2ID, link3ID],
      text: '',
      attributes: {
        'data-element': 'footer-links'
      }
    },
    
    // Link 1
    {
      id: link1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Link 1',
      parentId: links1ID,
      hierarchyName: "Footer Link 1",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'About Us',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Link 2
    {
      id: link2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Link 2',
      parentId: links1ID,
      hierarchyName: "Footer Link 2",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Our Team',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Link 3
    {
      id: link3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Link 3',
      parentId: links1ID,
      hierarchyName: "Footer Link 3",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Careers',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Column 3 - Links
    {
      id: column3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 3',
      parentId: columnsID,
      hierarchyName: "Footer Column 3",
      className: footerColumnClass,
      styles: {},
      children: [heading2ID, links2ID],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Heading 2
    {
      id: heading2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Footer Heading 2',
      parentId: column3ID,
      hierarchyName: "Footer Heading 2",
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Resources',
      attributes: {
        'data-element': 'footer-heading'
      }
    },
    
    // Links 2
    {
      id: links2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Links 2',
      parentId: column3ID,
      hierarchyName: "Footer Links 2",
      className: footerLinksClass,
      styles: {},
      children: [link4ID, link5ID, link6ID],
      text: '',
      attributes: {
        'data-element': 'footer-links'
      }
    },
    
    // Link 4
    {
      id: link4ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Link 4',
      parentId: links2ID,
      hierarchyName: "Footer Link 4",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Blog',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Link 5
    {
      id: link5ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Link 5',
      parentId: links2ID,
      hierarchyName: "Footer Link 5",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Documentation',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Link 6
    {
      id: link6ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Footer Link 6',
      parentId: links2ID,
      hierarchyName: "Footer Link 6",
      className: footerLinkClass,
      styles: {},
      children: [],
      text: 'Support',
      attributes: {
        'data-element': 'footer-link',
        'href': '#'
      }
    },
    
    // Column 4 - Social
    {
      id: column4ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Column 4',
      parentId: columnsID,
      hierarchyName: "Footer Column 4",
      className: footerColumnClass,
      styles: {},
      children: [heading3ID, socialLinksID],
      text: '',
      attributes: {
        'data-element': 'footer-column'
      }
    },
    
    // Heading 3
    {
      id: heading3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Footer Heading 3',
      parentId: column4ID,
      hierarchyName: "Footer Heading 3",
      className: footerHeadingClass,
      styles: {},
      children: [],
      text: 'Connect',
      attributes: {
        'data-element': 'footer-heading'
      }
    },
    
    // Social Links
    {
      id: socialLinksID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Social Links',
      parentId: column4ID,
      hierarchyName: "Social Links",
      className: footerSocialClass,
      styles: {},
      children: [social1ID, social2ID, social3ID],
      text: '',
      attributes: {
        'data-element': 'footer-social'
      }
    },
    
    // Social Link 1
    {
      id: social1ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 1',
      parentId: socialLinksID,
      hierarchyName: "Social Link 1",
      className: footerSocialLinkClass,
      styles: {},
      children: [social1IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Twitter'
      }
    },
    
    // Twitter Icon
    {
      id: social1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: social1ID,
      hierarchyName: "Twitter Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Twitter'
      }
    },
    
    // Social Link 2
    {
      id: social2ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 2',
      parentId: socialLinksID,
      hierarchyName: "Social Link 2",
      className: footerSocialLinkClass,
      styles: {},
      children: [social2IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Facebook'
      }
    },
    
    // Facebook Icon
    {
      id: social2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Facebook Icon',
      parentId: social2ID,
      hierarchyName: "Facebook Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:facebook.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Facebook'
      }
    },
    
    // Social Link 3
    {
      id: social3ID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Social Link 3',
      parentId: socialLinksID,
      hierarchyName: "Social Link 3",
      className: footerSocialLinkClass,
      styles: {},
      children: [social3IconId],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'aria-label': 'Instagram'
      }
    },
    
    // Instagram Icon
    {
      id: social3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Instagram Icon',
      parentId: social3ID,
      hierarchyName: "Instagram Icon",
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:instagram.svg?color=%23ffffff',
      attributes: {
        'data-element': 'social-icon-img',
        'alt': 'Instagram'
      }
    },
    
    // Bottom Section
    {
      id: bottomID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Footer Bottom',
      parentId: containerID,
      hierarchyName: "Footer Bottom",
      className: footerBottomClass,
      styles: {},
      children: [copyrightID],
      text: '',
      attributes: {
        'data-element': 'footer-bottom'
      }
    },
    
    // Copyright
    {
      id: copyrightID,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Footer Copyright',
      parentId: bottomID,
      hierarchyName: "Footer Copyright",
      className: footerCopyrightClass,
      styles: {},
      children: [],
      text: `© ${new Date().getFullYear()} YourBrand. All rights reserved.`,
      attributes: {
        'data-element': 'footer-copyright'
      }
    }
  ];
  
  // Return all elements
  return {
    footerSection: newElements[0],
    elements: newElements
  };
};

export default createFooterElements; 